import { useCallback } from 'react';
import useSWRInfinite from 'swr/infinite';
import { useReachBottom } from '~/app/_hooks/use-reach-bottom';
import { useTellerSession } from '~/common/hooks/use-auth/use-teller-session';
import type { UserOverview } from '~/common/types/user-overview';
import {
  fetchFollowListing,
  FOLLOW_USERS_PER_PAGE,
} from './requests/fetch-follow-listing';
import type { FollowListType } from './types';

export type FollowList = {
  userList: UserOverview[];
  nextCursor?: string;
  hasNextPage?: boolean;
};

export const useFetchFollowList = (
  userId: string,
  listType: FollowListType
) => {
  const { session } = useTellerSession();

  const getKey = useCallback(
    (_, prevData) => {
      if (!session?.uid) {
        return null;
      }
      if (!listType || !userId) {
        return null;
      }

      if (!prevData) {
        return [
          '/user/notifications',
          userId,
          listType,
          '',
          FOLLOW_USERS_PER_PAGE,
        ];
      }
      const nextCursor = prevData?.nextCursor;

      return [
        '/user/notifications',
        userId,
        listType,
        nextCursor,
        FOLLOW_USERS_PER_PAGE,
      ];
    },
    [session?.uid, listType, userId]
  );

  const fetcher = useCallback(
    ([_key, userId, listType, cursor, limit]: [
      string,
      string,
      FollowListType,
      string,
      number
    ]): Promise<FollowList> =>
      fetchFollowListing({
        userId,
        followListType: listType,
        cursor,
        limit,
      }),
    []
  );

  const {
    data: listingPage,
    error,
    size,
    setSize,
    isValidating,
  } = useSWRInfinite(getKey, fetcher);

  const isLoadingFirstPage = !listingPage && !error;

  // check if first page is empty
  const firstPage = listingPage?.[0];
  const isEmpty = firstPage !== undefined && firstPage?.userList?.length === 0;

  // check if we are in the last page
  const isLastPage = !listingPage?.[size - 1]?.hasNextPage || false;

  // check if are still loading data
  const isLoading =
    isLoadingFirstPage || (size > 0 && listingPage && !listingPage[size - 1]);

  // Detect scrolling to last result and trigger loading next page
  const handleOnReachBottom = useCallback(() => {
    if (!isValidating && !isLastPage && !isLoading) {
      setSize(size + 1);
    }
  }, [isLastPage, isLoading, isValidating, setSize, size]);
  const { bottomTargetRef: paginationRef } = useReachBottom({
    onReachBottom: handleOnReachBottom,
  });

  return {
    isLoading: isLoading || isValidating,
    isLoadingFirstPage,
    followData: listingPage
      ?.map((userListPage) => userListPage.userList)
      .flat(),
    isEmpty,
    error,
    paginationRef,
  };
};
